import { UploadStatus } from '@air/redux-uploader';

import {
  getImportDestinationBoard,
  getImportListItemTitle,
  getImportListStatusCopy,
  getImportUploadStatus,
  type ImporterTaskEnhanced,
  type NormalizedImportItem,
  normalizeLocalImport,
  normalizeRemoteImport,
} from '~/components/Import/utils';
import {
  type WorkspaceImportRow,
  type WorkspaceImportRowKind,
  type WorkspaceImportsCounts,
} from '~/components/v2/components/workspace/workspace-imports/workspaceImportTypes';
import { type ImporterTask } from '~/store/tasks/types';

export type BuildWorkspaceImportRowsParams = {
  disableResume: boolean;
  localTasks: ImporterTask[];
  remoteImports: ImporterTaskEnhanced[];
};

const workspaceImportRowKindFromUploadStatus = (status: UploadStatus): WorkspaceImportRowKind => {
  switch (status) {
    case UploadStatus.stopped:
      return 'stopped';
    case UploadStatus.completed:
      return 'completed';
    case UploadStatus.aborted:
      return 'cancelled';
    case UploadStatus.failed:
      return 'failed';
    default:
      return 'progressing';
  }
};

const toWorkspaceImportRow = (item: NormalizedImportItem, disableResume: boolean): WorkspaceImportRow => {
  const board = getImportDestinationBoard(item);
  const uploadStatus = getImportUploadStatus(item.status, item.storageExceeded);
  const kind = workspaceImportRowKindFromUploadStatus(uploadStatus);
  const thumbnailUrl = board.thumbnails?.[0] || undefined;

  return {
    board: { id: board.id, title: board.title },
    canCancel: item.canCancel,
    canResume: kind === 'stopped' && !disableResume,
    id: item.id,
    kind,
    source: item.source,
    statusCopy: getImportListStatusCopy(item),
    thumbnailUrl,
    title: getImportListItemTitle(item),
  };
};

const emptyWorkspaceImportCounts = (): WorkspaceImportsCounts => ({
  cancelledCount: 0,
  completedCount: 0,
  failedCount: 0,
  progressingCount: 0,
  stoppedCount: 0,
});

const countKind = (counts: WorkspaceImportsCounts, kind: WorkspaceImportRowKind) => {
  switch (kind) {
    case 'cancelled':
      counts.cancelledCount += 1;
      return;
    case 'completed':
      counts.completedCount += 1;
      return;
    case 'failed':
      counts.failedCount += 1;
      return;
    case 'stopped':
      counts.stoppedCount += 1;
      return;
    default:
      counts.progressingCount += 1;
  }
};

/** Rail badge/label counts. Skips row titles, thumbnails, and status copy. */
export const countWorkspaceImports = ({
  localTasks,
  remoteImports,
}: Omit<BuildWorkspaceImportRowsParams, 'disableResume'>): WorkspaceImportsCounts => {
  const counts = emptyWorkspaceImportCounts();
  const localRemoteIds = new Set(localTasks.flatMap((task) => (task.remoteTaskId ? [task.remoteTaskId] : [])));

  localTasks.forEach((task) => {
    const item = normalizeLocalImport(task);
    countKind(counts, workspaceImportRowKindFromUploadStatus(getImportUploadStatus(item.status, item.storageExceeded)));
  });

  remoteImports.forEach((item) => {
    if (localRemoteIds.has(item.id)) {
      return;
    }

    const normalized = normalizeRemoteImport(item);
    countKind(
      counts,
      workspaceImportRowKindFromUploadStatus(getImportUploadStatus(normalized.status, normalized.storageExceeded)),
    );
  });

  return counts;
};

export const buildWorkspaceImportRows = ({
  disableResume,
  localTasks,
  remoteImports,
}: BuildWorkspaceImportRowsParams): WorkspaceImportRow[] => {
  const localRemoteIds = new Set(localTasks.flatMap((task) => (task.remoteTaskId ? [task.remoteTaskId] : [])));
  const localRows = localTasks.map((task) => toWorkspaceImportRow(normalizeLocalImport(task), disableResume));
  const remoteRows = remoteImports
    .filter((item) => !localRemoteIds.has(item.id))
    .map((item) => toWorkspaceImportRow(normalizeRemoteImport(item), disableResume));

  return [...localRows, ...remoteRows];
};

export const groupWorkspaceImportRows = (rows: WorkspaceImportRow[]) => ({
  cancelledRows: rows.filter((row) => row.kind === 'cancelled'),
  completedRows: rows.filter((row) => row.kind === 'completed'),
  failedRows: rows.filter((row) => row.kind === 'failed'),
  inProgressRows: rows.filter((row) => row.kind === 'progressing' || row.kind === 'stopped'),
});
